import { mapYupErrorsToErrorMap, useNotifier } from "@repo/shared/common";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { createInvoiceSchema, createCreditNoteSchema } from "../types/InvoiceValidation";

import type { Invoice, CreditNote } from "../types/CommercialDocumentTypes";
import type { ErrorMap } from "@repo/shared/common";
import type { TFunction } from "next-i18next";
import type * as Yup from "yup";

export type ValidatorOptions<TIn> = {
	createSchema: (t: TFunction) => Yup.ObjectSchema<NonNullable<TIn>>;
};

export function useValidator<TIn extends object>(options: ValidatorOptions<TIn>) {
	const { t } = useTranslation();
	const [errors, setErrors] = useState<ErrorMap>({});
	const { notifyFailure } = useNotifier();

	// Memoize schema to avoid recreating it on every render
	// Extract createSchema from options to avoid object reference instability
	const { createSchema } = options;
	const schema = useMemo(() => createSchema(t), [createSchema, t]);

	// Memoize validate function to maintain stable reference
	const validate = useCallback(
		async (dataModel: NonNullable<TIn>): Promise<{ isValid: boolean; errors: ErrorMap }> => {
			try {
				await schema.validate(dataModel, { abortEarly: false });

				// No errors from schema validation
				const emptyErrors = {};
				setErrors(emptyErrors);
				return { isValid: true, errors: emptyErrors };
			} catch (err) {
				notifyFailure(t("validation-failed-plz-resolve-errors"));

				const errorMap = mapYupErrorsToErrorMap(err);
				//console.log("Validation errors:", errorMap);
				setErrors(errorMap);
				return { isValid: false, errors: errorMap };
			}
		},
		[schema, notifyFailure, t],
	);

	return { errors, setErrors, validate };
}

export function useInvoiceValidator() {
	return useValidator<Invoice>({
		createSchema: createInvoiceSchema,
	});
}

export function useCreditNoteValidator() {
	return useValidator<CreditNote>({
		createSchema: createCreditNoteSchema,
	});
}
